/***************************************************************************/
"use client";
/***************************************************************************/

import { VariantProps, cva } from "class-variance-authority";
import {
  AlertTriangle,
  CheckCircle,
  CircleAlert,
  Megaphone,
  Siren,
} from "lucide-react";
import * as React from "react";
import { cn } from "../../../utils/css";
import { AlertContext } from "./alert";

const alertIconVariants = cva("mt-0.5", {
  variants: {
    variant: {
      default: "",
      success: "text-success",
      info: "text-info",
      warning: "text-warning",
      error: "text-error",
    },
    size: {
      default: "[&_.lucide]:h-4 [&_.lucide]:w-4",
      sm: "[&_.lucide]:h-3 [&_.lucide]:w-3",
      lg: "[&_.lucide]:h-5 [&_.lucide]:w-5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const AlertDefaultIcon = Megaphone;
const AlertSuccessIcon = CheckCircle;
const AlertInfoIcon = CircleAlert;
const AlertWarningIcon = Siren;
const AlertDangerIcon = AlertTriangle;

const AlertIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertIconVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const contextValue = React.useContext(AlertContext);
  const derivedVariant = variant ?? contextValue.variant;
  const derivedSize = size ?? contextValue.size;

  return (
    <div
      ref={ref}
      className={cn(
        alertIconVariants({ variant: derivedVariant, size: derivedSize }),
        className,
      )}
      {...props}
    >
      {children ?? (
        <React.Fragment>
          {(!contextValue.variant || contextValue.variant === "default") && (
            <AlertDefaultIcon />
          )}
          {contextValue.variant === "success" && <AlertSuccessIcon />}
          {contextValue.variant === "info" && <AlertInfoIcon />}
          {contextValue.variant === "warning" && <AlertWarningIcon />}
          {contextValue.variant === "error" && <AlertDangerIcon />}
        </React.Fragment>
      )}
    </div>
  );
});
AlertIcon.displayName = "AlertIcon";

export {
  AlertDangerIcon,
  AlertDefaultIcon,
  AlertIcon,
  AlertInfoIcon,
  AlertSuccessIcon,
  AlertWarningIcon,
  alertIconVariants,
};
